import { useMutation } from '@apollo/client';
import { Button, Grid, List, ListItem, ListItemText, makeStyles, Paper, Typography } from '@material-ui/core';
import { UPLOAD_MEDIA } from 'apollo/mutations/Gallery';
import { GalleryContext } from 'context/gallery/GalleryContext';
import React, { useContext, useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';

interface IDropzoneFile {
    lastModified: number;
    lastModifiedDate: Record<string, unknown>;
    name: string;
    path: string;
    size: number;
    type: string;
    webkitRelativePath: string;
}

const useStyles = makeStyles(() => ({
    root: {
        margin: '25px 0',
    },
    text: {
        fontWeight: 700,
    },
    container: {
        padding: 50,
    },
}));

const FileDropzone: React.FC = () => {
    const classes = useStyles();
    const [files, setFiles] = useState<IDropzoneFile[] | null>(null);
    const [accFiles, setAccFiles] = useState<IDropzoneFile[] | null>(null);
    const [filesAreUploaded, setFilesAreUploaded] = useState<boolean>(false);
    const [erroredFiles, setErroredFiles] = useState<IDropzoneFile[]>();
    const [error, setError] = useState<string>();
    const [uploadMedia] = useMutation(UPLOAD_MEDIA);
    const { state: galleryState, setGalleryState } = useContext(GalleryContext);
    const [startUploading, setStartUploading] = useState(false);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const onDrop = (acceptedFiles: any, rejectedFiles: any) => {
        // Do something with the files
        if (rejectedFiles) {
            setErroredFiles(erroredFiles?.concat([...erroredFiles, rejectedFiles]));
        }
        setFilesAreUploaded(false);
        return setAccFiles(acceptedFiles);
    };
    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: 'image/jpg, image/png, image/jpeg, image/gif',
    });

    const RemoveFile = (name: string) => {
        setFiles(files && files.filter((file) => file.name !== name));
    };

    const uploadFiles = async () => {
        if (files?.length) {
            await uploadMedia({
                variables: {
                    path: galleryState?.breadcrumbs ? galleryState.breadcrumbs : '/',
                    fileName: files[0]?.name
                        .split('.png' || '.jpg' || '.jpeg' || '.gif')[0]
                        .toLowerCase()
                        .replace(/[\W_]/g, '-')
                        .toString(),
                    file: files[0],
                },
            })
                .then(async () => {
                    setStartUploading(true);
                    return await setFiles(files.filter((_file) => _file !== files[0]));
                })
                .catch((err) => console.error('err', err));
        }
    };

    const handleUploadMedia = async () => {
        setFilesAreUploaded(false);
        uploadFiles();
    };

    useEffect(() => {
        if (files?.length && startUploading) {
            setFilesAreUploaded(false);

            uploadFiles();
        }

        if (!files?.length && startUploading) {
            setFilesAreUploaded(true);
            setStartUploading(false);
        }
    }, [files]);

    useEffect(() => {
        if (filesAreUploaded) setGalleryState({ ...galleryState, hasUploaded: true });
    }, [filesAreUploaded]);

    useEffect(() => {
        if (files && accFiles) {
            const arr = [...files, ...accFiles];
            const filteredArray = arr.filter((item, pos) => arr.findIndex((p) => p.name === item.name) === pos);

            if (arr.length !== filteredArray.length) {
                setError('This file already exists');
            }
            setFiles(filteredArray);
        } else if (accFiles) {
            setFiles(accFiles);
        }
    }, [accFiles]);

    return (
        <Paper variant='outlined' elevation={5} className={classes.root}>
            {files && (
                <List component='nav' className={classes.root} aria-label='mailbox folders'>
                    {files.map((file: IDropzoneFile) => (
                        <ListItem divider={true} key={file.name}>
                            <ListItemText primary={file.name} />
                            <Button onClick={() => RemoveFile(file.name)}>Remove</Button>
                        </ListItem>
                    ))}
                    {filesAreUploaded && (
                        <Typography variant='subtitle2' align='center'>
                            The files are succesfully uploaded.
                        </Typography>
                    )}
                    {erroredFiles?.map((erroredFile: IDropzoneFile) => (
                        <p key={erroredFile.name}>The following file could not be uploaded: {erroredFile.name} </p>
                    ))}
                </List>
            )}
            <Grid
                container
                {...getRootProps()}
                className={classes.container}
                direction='column'
                justify='center'
                alignItems='center'
            >
                <input {...getInputProps()} />
                <p>{error}</p>
                {isDragActive ? (
                    <p className={classes.text}>Drop the files here ...</p>
                ) : (
                    <p className={classes.text}>Drag and drop some files here, or click to select files</p>
                )}
            </Grid>
            <Grid container justify='center'>
                <Button
                    variant='contained'
                    color={files?.length ? 'primary' : undefined}
                    onClick={() => handleUploadMedia()}
                    style={{ margin: 20 }}
                >
                    Upload files
                </Button>
            </Grid>
        </Paper>
    );
};

const UploadBlock: React.FC = () => {
    return <FileDropzone />;
};

export default UploadBlock;
